/**
 * `aethereum usage` — local token + cost report over the session index.
 *
 * Read-only and purely local: the source of truth is transcript
 * `message.usage` (Claude Code) and cumulative `token_count` diffs (Codex),
 * priced against the snapshot bundled in @br9704/aethereum-shared
 * (BerriAI/litellm, MIT). No network by default; `--refresh-prices` is the
 * one OPT-IN exception and fails soft back to the snapshot.
 */

import { BRAND, PRICING_ATTRIBUTION, PRICING_DISCLAIMER, estimateCostUsd } from "@br9704/aethereum-shared";
import {
  type SessionSummary,
  type SessionsCmdOptions,
  formatTokens,
  formatUsd,
  loadSessionSummaries,
} from "./sessions.js";
import type { TokenTotals } from "../sessions/index-store.js";
import { publishUsage } from "../sessions/publisher.js";
import { supportsColor } from "./splash.js";

/**
 * v6-S8: colour as a PUNCTUATION ACCENT, never load-bearing. Every table stays
 * fully readable with NO_COLOR set or when piped (non-TTY) — the accents only
 * tint the bar fill (green = the live/synced hue) and dim the empty track +
 * section chrome. `paint` returns the text unchanged when colour is off.
 */
const ESC = "\x1b[";
type Paint = (s: string, code: string) => string;
export interface AccentPaint {
  green: (s: string) => string;
  dim: (s: string) => string;
}
export function makePaint(useColor: boolean): AccentPaint {
  const paint: Paint = (s, code) => (useColor ? `${ESC}${code}m${s}${ESC}0m` : s);
  return { green: (s) => paint(s, "32"), dim: (s) => paint(s, "2") };
}
function colorEnabled(opts: { env?: Record<string, string | undefined> }): boolean {
  return supportsColor(opts.env ?? process.env, Boolean(process.stdout.isTTY));
}
/** A bar with a green fill + dim empty track when colour is on (accent only). */
export function accentBar(filled: number, width: number, c: AccentPaint): string {
  return c.green("█".repeat(filled)) + c.dim("·".repeat(Math.max(0, width - filled)));
}

const LITELLM_PRICES_URL =
  "https://raw.githubusercontent.com/BerriAI/litellm/main/model_prices_and_context_window.json";

interface RawPriceRow {
  input_cost_per_token?: number;
  output_cost_per_token?: number;
  cache_read_input_token_cost?: number;
  cache_creation_input_token_cost?: number;
}

export interface UsageCmdOptions extends SessionsCmdOptions {
  fetchImpl?: typeof fetch;
  /** cwd override (tests); used by --publish to resolve the project consent. */
  cwd?: string;
  /** `blocks --live` refresh interval (ms); default 2000. */
  refreshMs?: number;
  /** `blocks --live` render this many times then return (tests). Omit = run until interrupted. */
  maxTicks?: number;
  /** `blocks --live` clock per tick (tests); defaults to Date.now each render. */
  clock?: () => number;
}

type CostFn = (model: string, t: TokenTotals) => number | null;

/** Snapshot-first cost, optionally overridden by a live LiteLLM price map. */
function makeCostFn(live: Record<string, RawPriceRow> | null): CostFn {
  return (model, t) => {
    const row = live ? (live[model] ?? live[`anthropic/${model}`] ?? live[`openai/${model}`]) : undefined;
    if (row && typeof row.input_cost_per_token === "number" && typeof row.output_cost_per_token === "number") {
      const cr = typeof row.cache_read_input_token_cost === "number" ? row.cache_read_input_token_cost : row.input_cost_per_token;
      const cc = typeof row.cache_creation_input_token_cost === "number" ? row.cache_creation_input_token_cost : row.input_cost_per_token;
      return t.input * row.input_cost_per_token + t.output * row.output_cost_per_token + t.cacheRead * cr + t.cacheCreation * cc;
    }
    return estimateCostUsd(model, t);
  };
}

/** OPT-IN live price refresh; any failure silently falls back to the snapshot. */
async function fetchLivePrices(fetchImpl: typeof fetch): Promise<Record<string, RawPriceRow> | null> {
  try {
    const res = await fetchImpl(LITELLM_PRICES_URL, { signal: AbortSignal.timeout(5_000) });
    if (!res.ok) return null;
    const data = (await res.json()) as Record<string, RawPriceRow>;
    return data && typeof data === "object" ? data : null;
  } catch {
    return null;
  }
}

const zero = (): TokenTotals => ({ input: 0, output: 0, cacheRead: 0, cacheCreation: 0 });

function add(into: TokenTotals, t: TokenTotals): void {
  into.input += t.input;
  into.output += t.output;
  into.cacheRead += t.cacheRead;
  into.cacheCreation += t.cacheCreation;
}

const total = (t: TokenTotals): number => t.input + t.output + t.cacheRead + t.cacheCreation;

interface BreakdownRow {
  key: string;
  totals: TokenTotals;
  totalTokens: number;
  costUsd: number | null;
  sessions: number;
}

function localMidnight(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function pad(s: string, w: number): string {
  return s.length >= w ? s : s + " ".repeat(w - s.length);
}

/**
 * `aethereum usage --publish` — manual/scripted rollup publish with visible
 * output (the stop-hook path runs the same publisher silently). Numbers-only
 * wire schema, local consent + server flag double gate; see publisher.ts.
 */
async function runPublish(opts: UsageCmdOptions): Promise<number> {
  const res = await publishUsage({
    cwd: opts.cwd ?? process.cwd(),
    home: opts.home,
    now: opts.now,
    fetchImpl: opts.fetchImpl,
    force: true,
  });
  switch (res.status) {
    case "published":
      console.log(`Published ${res.inserted ?? res.rows} usage rollup row${(res.inserted ?? res.rows) === 1 ? "" : "s"} (numbers only: tokens, models, health counters).`);
      return 0;
    case "nothing":
      console.log("Nothing new to publish since the last rollup.");
      return 0;
    case "declined":
      console.log("Usage rollups are not enabled for this project. Opt in locally with `usageRollups: true` on this project's entry in ~/.aethereum/config.json (offered by `aethereum init`).");
      return 0;
    case "flag-off":
      console.log("The room declined the rollup (403): sharing usage metrics is off for this room. A room owner can enable it in the dashboard settings.");
      return 1;
    case "rate-limited":
      console.log("The usage endpoint is rate limited (429). Try again in a little while.");
      return 1;
    default:
      console.log("Could not reach the usage endpoint (offline or timed out). Your local data is untouched; try again later.");
      return 1;
  }
}

export async function runUsage(args: string[], opts: UsageCmdOptions = {}): Promise<number> {
  if (args.includes("--publish")) return runPublish(opts);
  if (args[0] === "blocks") return runUsageBlocks(args.slice(1), opts);

  const now = opts.now ?? Date.now();
  const c = makePaint(colorEnabled(opts));
  const week = args.includes("--week");
  const month = args.includes("--month");
  // Periods: today = since local midnight; --week = the last 7 days;
  // --month = the last 30 days.
  const periodStart = month ? now - 30 * 86_400_000 : week ? now - 7 * 86_400_000 : localMidnight(now);
  const periodLabel = month ? "last 30 days" : week ? "last 7 days" : "today";

  const byRepo = args.includes("--by-repo");
  const bySession = args.includes("--by-session");
  const breakdownKind: "model" | "repo" | "session" = byRepo ? "repo" : bySession ? "session" : "model";

  const live = args.includes("--refresh-prices") ? await fetchLivePrices(opts.fetchImpl ?? fetch) : null;
  const cost = makeCostFn(live);

  const all = await loadSessionSummaries(opts);
  const rows = all.filter((r) => (r.endedAt ?? r.startedAt ?? 0) >= periodStart);

  // Totals across the period.
  const totals = zero();
  const humanTokens = zero();
  const agentTokens = zero();
  let totalCost = 0;
  let anyCost = false;
  const unknownModels = new Set<string>();
  let activeMs = 0;
  let corrections = 0;
  let toolRetries = 0;
  let compactions = 0;
  for (const r of rows) {
    add(humanTokens, r.humanTokens);
    add(agentTokens, r.agentTokens);
    if (r.durationMs) activeMs += r.durationMs;
    corrections += r.heuristics.correctionLoopCount;
    toolRetries += r.heuristics.toolFailureRetryCount;
    compactions += r.heuristics.compactionCount;
    for (const [model, t] of Object.entries(r.models)) {
      add(totals, t);
      const c = cost(model, t);
      if (c === null) unknownModels.add(model);
      else {
        totalCost += c;
        anyCost = true;
      }
    }
  }
  const totalTokens = total(totals);
  const costUsd = anyCost ? totalCost : null;
  const blendedPerMTok = costUsd !== null && totalTokens > 0 ? (costUsd / totalTokens) * 1_000_000 : null;
  const outputTokensPerSec = activeMs > 0 ? totals.output / (activeMs / 1000) : null;

  // Breakdown rows.
  const groups = new Map<string, { totals: TokenTotals; costUsd: number | null; sessions: Set<string> }>();
  const groupAdd = (key: string, sessionId: string, model: string, t: TokenTotals) => {
    const g = groups.get(key) ?? { totals: zero(), costUsd: null, sessions: new Set<string>() };
    add(g.totals, t);
    const c = cost(model, t);
    if (c !== null) g.costUsd = (g.costUsd ?? 0) + c;
    g.sessions.add(sessionId);
    groups.set(key, g);
  };
  for (const r of rows) {
    for (const [model, t] of Object.entries(r.models)) {
      const key = breakdownKind === "model" ? model : breakdownKind === "repo" ? (r.repo ?? "(unknown repo)") : r.sessionId;
      groupAdd(key, r.sessionId, model, t);
    }
  }
  const breakdown: BreakdownRow[] = [...groups.entries()]
    .map(([key, g]) => ({
      key,
      totals: g.totals,
      totalTokens: total(g.totals),
      costUsd: g.costUsd,
      sessions: g.sessions.size,
    }))
    .sort((a, b) => b.totalTokens - a.totalTokens);

  if (args.includes("--json")) {
    console.log(
      JSON.stringify(
        {
          period: { label: periodLabel, start: periodStart, end: now },
          sessions: rows.length,
          totals,
          totalTokens,
          costUsd,
          blendedPerMTok,
          outputTokensPerSec,
          humanTokens,
          agentTokens,
          heuristics: { correctionLoopCount: corrections, toolFailureRetryCount: toolRetries, compactionCount: compactions },
          breakdownBy: breakdownKind,
          breakdown,
          unknownModels: [...unknownModels],
          pricesRefreshed: live !== null,
          disclaimer: PRICING_DISCLAIMER,
        },
        null,
        2,
      ),
    );
    return 0;
  }

  // v6-S8: a fixed-width, screenshot-friendly card for sharing. Estimates only,
  // local only; carries the human/agent split no personal usage tool has.
  if (args.includes("--compact")) {
    const W = 44;
    const line = (l: string) => `│ ${l.padEnd(W - 4)} │`;
    const rule = `${"─".repeat(W - 2)}`;
    const costStr = costUsd !== null ? formatUsd(costUsd) : "n/a";
    const blended = blendedPerMTok !== null ? ` (${formatUsd(blendedPerMTok)}/M)` : "";
    console.log(`┌${rule}┐`);
    console.log(line(`${BRAND.name} usage · ${periodLabel}`));
    console.log(`├${rule}┤`);
    console.log(line(`sessions   ${rows.length}`));
    console.log(line(`tokens     ${formatTokens(totalTokens)}`));
    console.log(line(`est. cost  ${costStr}${blended}`));
    console.log(line(`split      ${formatTokens(total(humanTokens))} human · ${formatTokens(total(agentTokens))} agent`));
    console.log(`└${rule}┘`);
    console.log(`  estimates at current prices, verify against your bill · local only`);
    return 0;
  }

  console.log(`Usage (${periodLabel}) · ${rows.length} session${rows.length === 1 ? "" : "s"}\n`);
  if (rows.length === 0) {
    console.log("No sessions in this period. Try --week or --month, or run your agent in a wired project first.");
    return 0;
  }

  console.log(`  Tokens     input ${formatTokens(totals.input)} · output ${formatTokens(totals.output)} · cache read ${formatTokens(totals.cacheRead)} · cache write ${formatTokens(totals.cacheCreation)}  (total ${formatTokens(totalTokens)})`);
  console.log(`  Split      human-initiated ${formatTokens(total(humanTokens))} · agent continuations ${formatTokens(total(agentTokens))}`);
  if (outputTokensPerSec !== null) console.log(`  Speed      ${outputTokensPerSec.toFixed(1)} output tokens/s across active session time`);
  if (costUsd !== null) {
    console.log(`  Est. cost  ${formatUsd(costUsd)}${blendedPerMTok !== null ? ` · blended ${formatUsd(blendedPerMTok)}/M tokens` : ""}`);
  } else {
    console.log("  Est. cost  unavailable (no model in this period is in the price snapshot)");
  }
  if (unknownModels.size > 0) {
    console.log(`  Note       unpriced models excluded from cost: ${[...unknownModels].join(", ")}`);
  }
  console.log(`  Friction   correction loops ${corrections} · tool-failure retries ${toolRetries} · compactions ${compactions}`);

  // v6-S8: per-day trend for the multi-day views, so `--week`/`--month` shows the
  // burn shape (the whole point of a usage tool), not just one collapsed total.
  if (week || month) {
    const perDay = new Map<string, number>();
    for (const r of rows) {
      const when = r.endedAt ?? r.startedAt ?? 0;
      if (!when) continue;
      const day = new Date(when).toISOString().slice(0, 10);
      let tokens = 0;
      for (const t of Object.values(r.models)) tokens += total(t);
      perDay.set(day, (perDay.get(day) ?? 0) + tokens);
    }
    if (perDay.size > 0) {
      const days = [...perDay.entries()].sort(([a], [b]) => a.localeCompare(b));
      const max = Math.max(...days.map(([, v]) => v), 1);
      const BAR_MAX = 24;
      console.log("\n  By day");
      for (const [day, tokens] of days) {
        const filled = Math.max(tokens > 0 ? 1 : 0, Math.round((tokens / max) * BAR_MAX));
        console.log(`  ${day}  ${accentBar(filled, BAR_MAX, c)}  ${pad(formatTokens(tokens), 8)}`);
      }
    }
  }

  const label = breakdownKind === "model" ? "MODEL" : breakdownKind === "repo" ? "REPO" : "SESSION";
  console.log(`\n  ${c.dim(pad(label, 34) + pad("TOKENS", 10) + pad("SESSIONS", 10) + "COST")}`);
  for (const row of breakdown) {
    const key = breakdownKind === "session" ? row.key.slice(0, 8) : row.key;
    console.log(`  ${pad(key.slice(0, 32), 34)}${pad(formatTokens(row.totalTokens), 10)}${pad(String(row.sessions), 10)}${row.costUsd !== null ? formatUsd(row.costUsd) : "-"}`);
  }

  if (args.includes("--verbose")) {
    console.log(`\n  Attribution: ${PRICING_ATTRIBUTION}.`);
    console.log("  Human vs agent split: an assistant turn directly answering a human prompt");
    console.log("  counts as human-initiated; turns continuing a tool loop count as agent.");
    if (args.includes("--refresh-prices") && live === null) {
      console.log("  Live price refresh failed; used the bundled snapshot instead.");
    }
  }
  console.log(`\n  Note: ${PRICING_DISCLAIMER}.`);
  return 0;
}

// ── `usage blocks` — 5-hour billing-window fuel gauge (v6-S8) ──────────────────

const BLOCK_MS = 5 * 60 * 60 * 1000; // Claude's 5-hour usage block.

/**
 * Bucket sessions into 5-hour windows (the Claude billing block) so a user can see
 * their burn shape and, for the CURRENT block, the burn rate + projected total +
 * time to reset: the "will I hit the limit before this task finishes" glance the
 * claude-code ecosystem loves. Local + estimates only.
 */
export async function runUsageBlocks(args: string[], opts: UsageCmdOptions = {}): Promise<number> {
  // v6-S8: `--live` re-renders the fuel gauge on an interval (a running fuel
  // gauge for a long task). Interval + tick-count + clock are injectable so it
  // is deterministically testable; a single render is the default path.
  if (args.includes("--live")) return runUsageBlocksLive(args, opts);

  const out = opts.out ?? ((l: string) => console.log(l));
  const c = makePaint(colorEnabled(opts));
  const now = opts.now ?? Date.now();
  const all = await loadSessionSummaries(opts);
  // Last 24h of blocks.
  const since = now - 24 * 60 * 60 * 1000;
  const perBlock = new Map<number, number>();
  for (const r of all) {
    const when = r.endedAt ?? r.startedAt ?? 0;
    if (!when || when < since) continue;
    let tokens = 0;
    for (const t of Object.values(r.models)) tokens += total(t);
    const block = Math.floor(when / BLOCK_MS);
    perBlock.set(block, (perBlock.get(block) ?? 0) + tokens);
  }

  if (args.includes("--json")) {
    const blocks = [...perBlock.entries()]
      .sort(([a], [b]) => a - b)
      .map(([b, tokens]) => ({ start: b * BLOCK_MS, tokens }));
    out(JSON.stringify({ blockMs: BLOCK_MS, now, blocks }, null, 2));
    return 0;
  }

  out("Usage by 5-hour block (last 24h, local, estimates)\n");
  if (perBlock.size === 0) {
    out("  No sessions in the last 24 hours.");
    return 0;
  }
  const blocks = [...perBlock.entries()].sort(([a], [b]) => a - b);
  const max = Math.max(...blocks.map(([, v]) => v), 1);
  const curBlock = Math.floor(now / BLOCK_MS);
  for (const [b, tokens] of blocks) {
    const startMs = b * BLOCK_MS;
    const label = new Date(startMs).toISOString().slice(11, 16);
    const filled = Math.max(1, Math.round((tokens / max) * 20));
    const here = b === curBlock ? c.green("  ← now") : "";
    out(`  ${label}  ${accentBar(filled, 20, c)}  ${pad(formatTokens(tokens), 8)}${here}`);
  }

  // Current-block fuel gauge: burn rate + projection + reset.
  const curTokens = perBlock.get(curBlock) ?? 0;
  const blockStart = curBlock * BLOCK_MS;
  const elapsedMin = Math.max(1, (now - blockStart) / 60000);
  const remainingMin = Math.max(0, (blockStart + BLOCK_MS - now) / 60000);
  const perMin = curTokens / elapsedMin;
  const projected = Math.round(perMin * (BLOCK_MS / 60000));
  out("");
  out(`  This block: ${formatTokens(curTokens)} so far · ${Math.round(perMin).toLocaleString()} tokens/min · projected ${formatTokens(projected)} · resets in ${Math.round(remainingMin)} min`);
  out(`\n  Note: ${PRICING_DISCLAIMER}.`);
  return 0;
}

/**
 * `usage blocks --live` — clear the screen and re-render the fuel gauge every
 * `refreshMs`. Bounded by `maxTicks` (for tests) or runs until the process is
 * interrupted. Each tick re-reads the index (long tasks accrue tokens) using a
 * fresh clock so the burn rate + reset countdown stay current.
 */
async function runUsageBlocksLive(args: string[], opts: UsageCmdOptions): Promise<number> {
  const write = opts.out ?? ((l: string) => console.log(l));
  const refreshMs = opts.refreshMs ?? 2000;
  const useColor = colorEnabled(opts);
  const innerArgs = args.filter((a) => a !== "--live");
  let tick = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Clear screen + home cursor (only on a colour-capable TTY; piped stays linear).
    if (useColor) write(`${ESC}2J${ESC}H`);
    const now = opts.clock ? opts.clock() : Date.now();
    write(`${makePaint(useColor).dim(`live · refresh ${Math.round(refreshMs / 1000)}s · ${new Date(now).toISOString().slice(11, 19)}`)}`);
    await runUsageBlocks(innerArgs, { ...opts, now, out: write });
    tick++;
    if (opts.maxTicks !== undefined && tick >= opts.maxTicks) return 0;
    await new Promise((r) => setTimeout(r, refreshMs));
  }
}

export type { SessionSummary };
